import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AudioWaveform, Eye, LogOut, User, Briefcase, Smartphone, History, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import AnalysisPage from "@/pages/AnalysisPage";
import VisionAIPage from "@/pages/VisionAIPage";
import HistoryPage from "@/pages/HistoryPage";
import AnalysisDetailPage from "@/pages/AnalysisDetailPage";
import B2BShowcasePage from "@/pages/B2BShowcasePage";
import LoginPage from "@/pages/LoginPage";
import MobileReportsPage from "@/pages/MobileReportsPage";
import PrintReportPage from "@/pages/PrintReportPage";
import MFCWLPage from "@/pages/MFCWLPage";
import MFCWLTestPage from "@/pages/MFCWLTestPage";
import PitchDeckPage from "@/pages/PitchDeckPage";
import api from "@/lib/api";

const NavLink = ({ to, children, icon: Icon }) => {
  const location = useLocation();
  const isActive = location.pathname === to || (to !== "/" && location.pathname.startsWith(to));
  return (
    <Link
      to={to}
      data-testid={`nav-link-${to.replace(/\//g, '') || 'home'}`}
      className={`flex items-center gap-2 px-5 py-2.5 text-base font-bold uppercase tracking-wider transition-all duration-200 rounded-sm ${
        isActive
          ? "bg-slate-900 text-white"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      }`}
    >
      <Icon size={16} />
      {children}
    </Link>
  );
};

const Header = ({ user, onLogout }) => (
  <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-black/5">
    <div className="max-w-[1600px] mx-auto px-4 md:px-6 flex items-center justify-between h-16">
      <div className="flex items-center gap-3">
        <img 
          src="/wisedrive-logo-header.png" 
          alt="Wisedrive" 
          className="h-8"
          data-testid="app-logo"
        />
      </div>
      <div className="flex items-center gap-4">
        <nav className="flex items-center gap-2" data-testid="main-nav">
          <NavLink to="/mfcwl" icon={Shield}>MFCWL</NavLink>
          <NavLink to="/" icon={AudioWaveform}>Sound AI</NavLink>
          <NavLink to="/vision" icon={Eye}>Vision AI</NavLink>
          <NavLink to="/mobile-reports" icon={Smartphone}>Mobile Reports</NavLink>
          <NavLink to="/history" icon={History}>History</NavLink>
        </nav>
        <div className="flex items-center gap-3 pl-4 border-l border-slate-200">
          {user && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <div className="w-8 h-8 rounded-full bg-[#003399] flex items-center justify-center">
                <User size={14} className="text-white" />
              </div>
              <span className="hidden md:inline font-medium">{user.name}</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onLogout}
            className="text-slate-500 hover:text-red-600 hover:bg-red-50"
            data-testid="logout-btn"
          >
            <LogOut size={16} className="mr-2" />
            Logout
          </Button>
        </div>
      </div>
    </div>
  </header>
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if this is the print route or pitch deck - render without auth
  const isPrintRoute = window.location.pathname.startsWith('/print/');
  const isPitchDeck = window.location.pathname === '/pitch-deck';

  useEffect(() => {
    if (isPrintRoute) {
      setIsLoading(false);
      return;
    }
    // Check if user is already logged in
    const checkAuth = async () => {
      const token = api.getToken();
      const storedUser = localStorage.getItem("wisedrive_user");
      
      if (token && storedUser) {
        try {
          // Verify token is still valid
          const response = await api.getMe();
          setUser(response.data);
          setIsAuthenticated(true);
        } catch (err) {
          // Token invalid, clear auth
          api.clearTokens();
          setIsAuthenticated(false);
          setUser(null);
        }
      } else {
        // Check legacy auth for backward compatibility
        const legacyAuth = localStorage.getItem("wisedrive_auth");
        if (legacyAuth === "true" && storedUser) {
          setUser(JSON.parse(storedUser));
          setIsAuthenticated(true);
        }
      }
      
      setIsLoading(false);
    };
    
    checkAuth();
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await api.logout();
    } catch (err) {
      // Ignore logout errors
    }
    api.clearTokens();
    setIsAuthenticated(false);
    setUser(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#003399] border-t-transparent rounded-full animate-spin" />
          <div className="text-slate-600 text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  // Print route - no auth required
  if (isPrintRoute) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/print/:id" element={<PrintReportPage />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Pitch deck - no auth required, fullscreen presentation
  if (isPitchDeck) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/pitch-deck" element={<PitchDeckPage />} />
        </Routes>
      </BrowserRouter>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-[#F8F9FA] bg-grid-pattern">
        <Header user={user} onLogout={handleLogout} />
        <main className="max-w-[1600px] mx-auto p-4 md:p-6">
          <Routes>
            <Route path="/" element={<AnalysisPage />} />
            <Route path="/mfcwl" element={<MFCWLPage />} />
            <Route path="/mfcwl/test/:id" element={<MFCWLTestPage />} />
            <Route path="/vision" element={<VisionAIPage />} />
            <Route path="/mobile-reports" element={<MobileReportsPage />} />
            <Route path="/b2b" element={<B2BShowcasePage />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/analysis/:id" element={<AnalysisDetailPage />} />
          </Routes>
        </main>
        <Toaster position="bottom-right" />
      </div>
    </BrowserRouter>
  );
}

export default App;
